<template>
  <div>
    <div class="mt-2">
      <naj-search-box
        :type-id="formFieldType.number"
        :placeholder="local.home.tabCommon.filterWithDecisionCode"
        :search.sync="searchQuery.decisionCode"
        @filterDataClicked="filterData()"
        @showFilterClicked="toggleFilter()"
      ></naj-search-box>
    </div>
    <div
      v-if="hasFilter"
      class="filter-row-wrapper d-flex mt-3"
    >
      <div class="col-12">
        <template v-for="filter in filters">
          <span
            v-if="searchQuery[filter.name]"
            :key="filter.name"
            class="ma-1"
          >
            <v-chip
              :key="filter.name"
              :input-value="searchQuery[filter.name]"
              color="#F7FBFB"
              close
              outlined
              @click:close="searchQuery[filter.name]=null ; filterData();"
            >
              <strong class="text-primary">{{ filter.label + ':' + (searchQuery[filter.name+'DisplayName'] || searchQuery[filter.name]) }}</strong>
            </v-chip>
          </span>
        </template>
      </div>
    </div>
    <v-row class="mt-7">
      <v-col :cols="showFilter?9:12">
        <!-- TODO : items-per-page should be updated after najiz shutdown (20 for case + 20 for icms)-->
        <naj-item-table
          :data-items="data.list"
          :headers="headers"
          :pagination="pagination"
          :loading="loading"
          :items-per-page="20"
          :server-items-length="pagination.totalCount"
          @update:page="onPageChange"
        >
          <template
            slot="decisionCode"
            slot-scope="{item}"
          >
            <div class="info-item-details-title">
              {{ local.home.tabCommon.decisionCode }}
            </div>
            <div class="info-item-details-value mt-2">
              {{ item.decisionCode }}
            </div>
          </template>
          <template
            slot="action"
            slot-scope="{item}"
          >
            <div class="mx-4">
              <v-menu offset-y>
                <template v-slot:activator="{ on }">
                  <v-icon v-on="on">
                    mdi-dots-vertical
                  </v-icon>
                </template>
                <v-list>
                  <v-list-item>
                    <v-list-item-title>
                      <router-link :to="`/cases/case-file/${item.isPega}/${item.caseObjectKey}`">
                        <naj-button
                          text
                          type="button"
                          size="small"
                        >
                          {{ local.home.tabCommon.showCaseFile }}
                        </naj-button>
                      </router-link>
                    </v-list-item-title>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>
          </template>
        </naj-item-table>
      </v-col>
      <v-col
        v-if="showFilter"
        cols="3"
      >
        <v-card color="pa-3">
          <v-row>
            <v-col class="pt-0">
              <div class="text-left">
                <v-icon
                  class="icon"
                  @click="toggleFilter()"
                >
                  mdi-close
                </v-icon>
              </div>
              <div class="text-h4 font-weight-bold">
                {{ local.common.filter }}
              </div>
            </v-col>
            <v-col
              v-for="filter in filters"
              :key="filter.name"
              cols="12"
            >
              <naj-input
                :type-id="filter.typeId"
                :label="filter.label"
                :value.sync="searchQuery[filter.name]"
                :value-name.sync="searchQuery[filter.name+'DisplayName']"
                :items="filter.items"
                item-text="name"
                item-value="id"
              ></naj-input>
            </v-col>

            <v-col
              cols="12"
              class="text-center"
            >
              <naj-button
                :loading="loading"
                @click="filterData()"
              >
                {{ local.common.filter }}
              </naj-button>

              <naj-button
                outlined
                @click="toggleFilter()"
              >
                {{ local.common.close }}
              </naj-button>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>
  </div>
</template>

<script>

import { createNamespacedHelpers } from 'vuex';
const { mapActions: mapCasesActions } = createNamespacedHelpers('CasesModule');
const { mapState: mapLUTState, mapActions: mapLUTActions } = createNamespacedHelpers(
  'LookupsModule'
);
import { toHijriDate } from '@/helpers/utils';
import { userType, casePartyRoleEnum } from '@/helpers/enum';

export default {
  name: 'DecisionsTab',
  components: {},
  props: {
    userInfo: {
      type: Object,
      required: true,
    },
    itemsPerPage: {
      default: 10,
      type: Number,
      required: false,
    },
    noDataText: {
      type: String,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      searchQuery: {
        decisionCode: null,
        courtId: null,
        toDecisionDate: null,
        toRequestDate: null,
        decisionTypeId: null,
      },
      loading: false,
      userType,
      showFilter: false,
      data: { list: [] },
      pagination: {
        pageSize: 20,
        pageIndex: 0,
        totalCount: 0,
        totalPages: 0,
        hasPreviousPage: false,
        hasNextPage: false,
      },
      headers: [
        {
          value: 'decisionCode',
          text: this.local.home.tabCommon.decisionCode,
          isCustomTemplate: true,
          class: 'decisionCode-header',
        },
        {
          value: 'caseCode',
          text: this.local.home.tabCommon.caseCode,
        },
        {
          value: 'decisionDate',
          text: this.local.home.tabCommon.decisionDate,
          formatter: toHijriDate,
        },
        {
          value: 'decisionType',
          text: this.local.home.tabCommon.decisionType,
        },
        {
          value: 'decisionStatus',
          text: this.local.home.tabCommon.decisionStatus,
        },
        {
          value: 'courtName',
          text: this.local.home.tabCommon.courtName,
        },
        {
          value: 'subCircleName',
          text: this.local.home.tabCommon.subCircleName,
        },
        {
          value: 'action',
          class: 'action-item justify-content-center',
          isCustomTemplate: true,
        },
      ],
    };
  },
  computed: {
    ...mapLUTState(['courts', 'decisionTypes', 'portalPartyRoles']),
    filters() {
      return [
        this.mapFilterItem('courtId', 'court', 'selectbox', this.courts),
        this.mapFilterItem('decisionTypeId', 'decisionType', 'selectbox', this.decisionTypes),
        this.filterRoleInCase(),
        this.mapFilterItem('fromDecisionDate', 'fromDate', 'date'),
        this.mapFilterItem('toDecisionDate', 'toDate', 'date'),
      ];
    },
    hasFilter() {
      return this.searchQuery.courtId ||
        this.searchQuery.toDecisionDate ||
        this.searchQuery.toRequestDate ||
        this.searchQuery.roleInCase ||
        this.searchQuery.decisionTypeId;
    },
    computedSearchQuery() {
      return Object.assign({}, this.searchQuery);
    },
  },
  watch: {
    computedSearchQuery: {
      deep: true,
      handler(newValue, oldValue) {
        const oldValueString = JSON.stringify({
          ...oldValue,
          decisionCode: null,
        });
        const newValueString = JSON.stringify({
          ...newValue,
          decisionCode: null,
        });
        const isChanged = oldValueString != newValueString;
        if (isChanged) {
          this.filterData();
        }
      },
    },
  },
  mounted() { this.init(); },
  methods: {
    ...mapLUTActions(['getCourts', 'getDecisionTypes', 'getPortalPartyRoles']),
    filterRoleInCase() {
      if (this.userInfo.userType !== this.userType.najizLawyer) {
        let filteredPartyRoles = this.portalPartyRoles;
        // if (this.userInfo.userType === this.userType.najizCitizen || this.userInfo.userType === this.userType.najizServicesCenter)
        //   filteredPartyRoles = this.portalPartyRoles.filter(x => x.id !== casePartyRoleEnum.lawyer && x.id !== casePartyRoleEnum.representative);
        // else
        //   filteredPartyRoles = this.portalPartyRoles;
        return this.mapFilterItem('roleInCase', 'roleInCase', 'selectbox', filteredPartyRoles);
      }
      else
        return '';
    },
    mapFilterItem(name, key, typeId, items = []) {
      return {
        name: name,
        label: this.local.home.filter[key],
        typeId: this.formFieldType[typeId],
        items,
      };
    },
    toggleFilter() {
      this.showFilter = !this.showFilter;
    },
    ...mapCasesActions(['getDecisions']),
    init() {
      this.pagination.pageIndex = 0;
      this.searchQuery.decisionCode = null;
      this.loadData();
      this.getCourts();
      this.getDecisionTypes();
      this.getPortalPartyRoles();
    },
    loadData() {
      this.loading = true;
      this.getDecisions({
        ...this.searchQuery,
        ...this.pagination,
      })
        .then(({ data }) => {
          this.loading = false;
          this.data = data;
          this.pagination = {
            pageIndex: data.pageIndex,
            pageSize: data.pageSize,
            totalCount: data.totalCount,
            totalPages: data.totalPages,
            hasPreviousPage: data.hasPreviousPage,
            hasNextPage: data.hasNextPage,
          };
        })
        .finally(() => {
          this.loading = false;
          this.$emit('updateTabCount', this.pagination.totalCount);
        });
    },
    filterData() {
      this.pagination.pageIndex = 0;
      this.loadData();
    },
    onPageChange(pageIndex) {
      this.pagination.pageIndex = pageIndex - 1;
      this.loadData();
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep .List__empty {
  margin-top: 0px !important;
}
::v-deep .info-item-box .info-item-details:nth-child(1) {
  background: var(--app-light-secondary-color) 0% 0% no-repeat padding-box;
}

.filter-row-wrapper {
  background: #ffffff 0% 0% no-repeat padding-box;
  box-shadow: 0px 10px 30px #d1d5df80;
}

::v-deep .v-chip.v-chip--outlined.v-chip.v-chip {
  background-color: #f7fbfb !important;
  border: 1px solid var(--app-primary-color) !important;
  border-radius: unset;

  .v-chip__content {
    button {
      color: var(--app-primary-color);
    }
  }
}

::v-deep .info-item-details.decisionCode-header {
  .info-item-details-value {
    font-size: 24px;
  }
}
</style>
